import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { dashboardAPI, assignmentsAPI, quizzesAPI, sopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, 
  FileQuestion, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Users,
  GraduationCap,
  Target,
  ArrowRight,
  ClipboardCheck,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

// Stryde Research Icon
const STRYDE_ICON = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/x579wpam_stryde-fin-02.jpg";

const Dashboard = () => {
  const { user, canManage } = useAuth();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [sopCompletions, setSopCompletions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [
        dashboardAPI.getStats(),
        assignmentsAPI.getAll(),
        quizzesAPI.getMyAttempts()
      ];
      
      // Load onboarding progress for employees
      if (!canManage) {
        promises.push(
          api.get('/my-onboarding-completions').catch(() => ({ data: [] })),
          api.get('/onboarding-checklist').catch(() => ({ data: [] })),
          sopsAPI.getMyCompletions().catch(() => ({ data: [] }))
        );
      }

      const results = await Promise.all(promises);
      setStats(results[0].data);
      setAssignments(results[1].data.slice(0, 5));
      setRecentQuizzes(results[2].data.slice(-5).reverse());
      
      if (!canManage && results[3] && results[4]) {
        const completions = results[3].data || [];
        const items = results[4].data || [];
        setOnboardingProgress({
          completed: completions.length,
          total: items.length,
          percent: items.length > 0 ? Math.round((completions.length / items.length) * 100) : 0
        });
        setSopCompletions(results[5]?.data || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Calculate next action items for employees
  const getNextActions = () => {
    const actions = [];
    
    // Check onboarding progress
    if (onboardingProgress && onboardingProgress.percent < 100) {
      actions.push({
        type: 'onboarding',
        label: 'Onboarding Checklist',
        description: `${onboardingProgress.total - onboardingProgress.completed} items remaining`,
        link: '/onboarding',
        priority: 'high'
      });
    }
    
    // Check pending trainings
    const pendingTrainings = stats?.total_assignments - stats?.completed_assignments || 0;
    if (pendingTrainings > 0) {
      actions.push({
        type: 'training',
        label: 'Training Videos',
        description: `${pendingTrainings} videos to watch`,
        link: '/trainings',
        priority: 'medium'
      });
    }
    
    // Check pending SOPs
    const totalSops = stats?.total_sops || 13;
    const completedSops = sopCompletions.length || 0;
    if (completedSops < totalSops) {
      actions.push({
        type: 'sop',
        label: 'SOP Training',
        description: `${totalSops - completedSops} SOPs + quizzes pending`,
        link: '/sops',
        priority: 'medium'
      });
    }
    
    return actions;
  };

  // Employee Dashboard
  if (!canManage) {
    const nextActions = getNextActions();
    const totalProgress = Math.round(
      ((onboardingProgress?.percent || 0) + 
       (stats?.completion_rate || 0) + 
       ((sopCompletions.length / 13) * 100)) / 3
    );

    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
        {/* Employee Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">
              {user?.job_role ? `${user.job_role} Onboarding` : 'Your learning journey'}
            </p>
          </div>
          <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">
            {totalProgress}% Complete
          </Badge>
        </div>

        {/* Next Action CTA - Most prominent element */}
        {nextActions.length > 0 && (
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white shadow-card" data-testid="next-action-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-slate-900">What to do next</h2>
                  <p className="text-sm text-slate-500">
                    {nextActions.length} {nextActions.length === 1 ? 'item' : 'items'} need your attention
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                {nextActions.slice(0, 3).map((action, idx) => (
                  <Link key={idx} to={action.link} className="block">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all hover:shadow-md ${
                      action.priority === 'high' 
                        ? 'border-amber-200 bg-amber-50 hover:border-amber-300' 
                        : 'border-slate-200 bg-white hover:border-blue-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          action.type === 'onboarding' ? 'bg-amber-100' :
                          action.type === 'training' ? 'bg-blue-100' : 'bg-emerald-100'
                        }`}>
                          {action.type === 'onboarding' && <ClipboardCheck className="w-4 h-4 text-amber-600" />}
                          {action.type === 'training' && <GraduationCap className="w-4 h-4 text-blue-600" />}
                          {action.type === 'sop' && <BookOpen className="w-4 h-4 text-emerald-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{action.label}</p>
                          <p className="text-sm text-slate-500">{action.description}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>

              <Link to="/onboarding">
                <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700" data-testid="continue-onboarding-btn">
                  Continue Onboarding
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* All Complete State */}
        {nextActions.length === 0 && (
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-heading font-bold text-slate-900">All caught up!</h2>
              <p className="text-slate-600 mt-1">You've completed all your onboarding tasks</p>
              <Link to="/reports">
                <Button variant="outline" className="mt-4">
                  View Your Training Report
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Onboarding</span>
                <span className="text-sm font-bold text-blue-600">{onboardingProgress?.percent || 0}%</span>
              </div>
              <Progress value={onboardingProgress?.percent || 0} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                {onboardingProgress?.completed || 0} of {onboardingProgress?.total || 0} items
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Trainings</span>
                <span className="text-sm font-bold text-emerald-600">{stats?.completion_rate || 0}%</span>
              </div>
              <Progress value={stats?.completion_rate || 0} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                {stats?.completed_assignments || 0} of {stats?.total_assignments || 0} completed
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">SOP Quizzes</span>
                <span className="text-sm font-bold text-amber-600">
                  {Math.round((sopCompletions.length / 13) * 100)}%
                </span>
              </div>
              <Progress value={(sopCompletions.length / 13) * 100} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                {sopCompletions.length} of 13 passed
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
              <FileQuestion className="w-5 h-5 text-blue-600" />
              Recent Quiz Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentQuizzes.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <FileQuestion className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No quiz attempts yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentQuizzes.slice(0, 3).map((attempt) => (
                  <div key={attempt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        attempt.passed ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {attempt.passed ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {attempt.passed ? 'Quiz Passed' : 'Quiz Failed'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(attempt.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`text-lg font-bold ${
                      attempt.passed ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {attempt.score}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin/Lead Dashboard
  const adminCards = [
    { 
      title: 'Total Users', 
      value: stats?.total_users || 0, 
      subtitle: `${stats?.total_employees || 0} employees`,
      icon: Users,
      color: 'bg-blue-500'
    },
    { 
      title: 'Training Modules', 
      value: stats?.total_training_nodes || 0, 
      subtitle: 'Available trainings',
      icon: GraduationCap,
      color: 'bg-emerald-500'
    },
    { 
      title: 'Assessments', 
      value: stats?.total_quizzes || 0, 
      subtitle: 'Available tests',
      icon: FileQuestion,
      color: 'bg-amber-500'
    },
    { 
      title: 'Completion Rate', 
      value: `${stats?.completion_rate || 0}%`, 
      subtitle: `${stats?.completed_assignments || 0}/${stats?.total_assignments || 0} assignments`,
      icon: TrendingUp,
      color: 'bg-violet-500'
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Admin Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900">
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-slate-600 mt-1">
          Overview of your learning management system
        </p>
      </div>

      {/* Admin Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {adminCards.map((card, index) => (
          <Card key={index} className="border-slate-200 shadow-card hover:shadow-float transition-shadow duration-300">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{card.title}</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-2">{card.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Assignments */}
        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Recent Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {assignments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No assignments yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => {
                  const progress = assignment.curriculum_id ? 
                    Math.round(((assignment.completed_training_nodes?.length || 0) / 5) * 100) : 0;
                  return (
                    <div key={assignment.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        assignment.status === 'completed' ? 'bg-emerald-100' : 'bg-blue-100'
                      }`}>
                        {assignment.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {assignment.curriculum_title || 'Curriculum'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={progress} className="h-1.5 flex-1" />
                          <span className="text-xs text-slate-500">{progress}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Link to="/assignments">
              <Button variant="outline" className="w-full mt-4" data-testid="view-all-assignments-btn">
                View All Assignments
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Quiz Attempts */}
        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
              <FileQuestion className="w-5 h-5 text-blue-600" />
              Recent Quiz Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentQuizzes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileQuestion className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No quiz attempts yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentQuizzes.map((attempt) => (
                  <div key={attempt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        attempt.passed ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {attempt.passed ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Target className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Quiz Attempt</p>
                        <p className="text-xs text-slate-500">
                          {new Date(attempt.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${
                      attempt.passed ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {attempt.score}%
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/onboarding-dashboard">
              <Button variant="outline" className="w-full mt-4" data-testid="view-team-progress-btn">
                View Team Progress
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200 shadow-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-heading font-semibold text-slate-900">
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/users">
              <Button variant="outline" className="w-full h-24 flex-col gap-2 hover:bg-blue-50 hover:border-indigo-300">
                <Users className="w-6 h-6 text-blue-600" />
                <span>Add User</span>
              </Button>
            </Link>
            <Link to="/curricula">
              <Button variant="outline" className="w-full h-24 flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-300">
                <GraduationCap className="w-6 h-6 text-emerald-600" />
                <span>Curricula</span>
              </Button>
            </Link>
            <Link to="/onboarding-dashboard">
              <Button variant="outline" className="w-full h-24 flex-col gap-2 hover:bg-amber-50 hover:border-amber-300">
                <TrendingUp className="w-6 h-6 text-amber-600" />
                <span>Team Progress</span>
              </Button>
            </Link>
            <Link to="/reports">
              <Button variant="outline" className="w-full h-24 flex-col gap-2 hover:bg-violet-50 hover:border-violet-300">
                <BookOpen className="w-6 h-6 text-violet-600" />
                <span>Reports</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
